import {
  isIfConditionStep,
  isPlaceholderStep,
  TriggerCreate,
  TriggerForEditor,
  TriggerStepCreate,
} from "@dots-projects/types";
import invariant from "ts-invariant";
import { getAllParentSteps } from "../../../../../../libs/types/src/lib/getAvailableVariables";

const stepsThatTriggerAnInteraction = [
  "send_survey",
  "open_modal",
  "send_multiple_select",
];
import { nodeSizes } from "../createLayout";
import { FlowNodeType } from "../TriggerFlow";
import { isOverlap, TypedNode } from "./stateUtils";

export function canStepBeEphemeral(
  trigger: TriggerCreate | TriggerForEditor,
  step: TriggerStepCreate
) {
  const parentSteps: TriggerStepCreate[] = getAllParentSteps(trigger, step);
  const parentTriggerAnInteraction = parentSteps.some((s) =>
    stepsThatTriggerAnInteraction.includes(s.type)
  );
  const triggerTypeTriggerAnInteraction = ["button_click", "command"].includes(
    (trigger as any).liveTriggerConfigs?.type
  );
  return parentTriggerAnInteraction || triggerTypeTriggerAnInteraction;
}

/**
 * Check if an ephemeral step can be added, conditions:
 * first step of a button_click trigger OR if the step before the test captcha creates an interaction
 */
export function canAddEphemeralStep(
  trigger: TriggerCreate | TriggerForEditor,
  parentStepId: string
) {
  const parentStepTriggersAnInteraction =
    stepsThatTriggerAnInteraction.includes(parentStepId);
  const triggerTypeTriggersAnInteraction = ["button_click", "command"].includes(
    (trigger as any).liveTriggerConfigs?.type
  );
  const firstStep = !Boolean(trigger.steps.length);
  return (
    parentStepTriggersAnInteraction ||
    (firstStep && triggerTypeTriggersAnInteraction)
  );
}

/**
 * Perform a DFS (depth-first search) on the given graph
 *
 * @param entry entry step
 * @param graph
 * @param onVisit we can short-curcuit the DFS by returning `false` from the onVisit function.
 */
export function dfsSteps(
  entry: TriggerStepCreate,
  graph: TriggerStepCreate[],
  onVisit: (step: TriggerStepCreate) => any
) {
  const visited = new Set<string>();
  const idStepMap = new Map<string, TriggerStepCreate>();
  graph.forEach((s) => {
    idStepMap.set(s.id, s);
  });
  const getNeighbors = (step: TriggerStepCreate) => {
    const ns: (TriggerStepCreate | undefined)[] = [];
    if (step.if?.elseStepId) {
      ns.push(idStepMap.get(step.if.elseStepId));
    }
    ns.push(...step.transitions.map((t) => idStepMap.get(t.targetStepId)));
    return ns.filter((n) => n) as TriggerStepCreate[];
  };
  const dfs = (step: TriggerStepCreate) => {
    visited.add(step.id);
    if (onVisit(step) === false) return; // shortcurcuit
    const neighbors = getNeighbors(step);
    neighbors.forEach((n) => {
      if (!visited.has(n.id)) dfs(n);
    });
  };
  dfs(entry);
}

export function getParents(
  step: TriggerStepCreate,
  graph: TriggerStepCreate[]
) {
  return graph.filter((s) =>
    s.transitions.some((t) => t.targetStepId === step.id)
  );
}

export function getEntryStep(graph: TriggerStepCreate[]) {
  return graph.find((s) => s.isEntry) ?? graph[0];
}

/**
 * Find the "roots" (steps that don't have a parent) with a path that includes the given step
 *
 * There could be multiple roots for a step. A step could be its own root.
 *
 * Algorithm:
 * 	1. dfs starting from entry step, if hits the given node, then entry is the root
 *  2. otherwise, dfs from all other steps that are not visited yet
 *
 * @param step
 * @param graph
 * @returns an array of root steps
 */
export function getRoots(step: TriggerStepCreate, graph: TriggerStepCreate[]) {
  invariant(step, `step is undefined or null`);
  const entry = getEntryStep(graph);
  const seenFromEntry = new Set<string>();
  dfsSteps(entry, graph, (step) => {
    seenFromEntry.add(step.id);
  });
  const roots = new Set<TriggerStepCreate>();
  if (seenFromEntry.has(step.id)) roots.add(entry);

  const otherRoots = new Set(graph.filter((s) => !seenFromEntry.has(s.id)));
  const otherRootsIter = otherRoots.entries();
  let otherRootIterResult;
  while (!(otherRootIterResult = otherRootsIter.next()).done) {
    const otherRoot = otherRootIterResult.value[0];
    let seenTarget = false;
    dfsSteps(otherRoot, graph, (s) => {
      if (s.id === step.id) {
        seenTarget = true;
      }
      otherRoots.delete(s);
    });
    if (seenTarget) {
      roots.add(otherRoot);
    }
  }

  return Array.from(roots);
}

/**
 * Collect the steps that can be reached when traversing the graph from `startingStep`
 *
 * @param startingStep
 * @param graph
 * @returns an array of downstream steps (including `startingStep`)
 */
export function collectDownstreamSteps(
  startingStep: TriggerStepCreate | undefined,
  graph: TriggerStepCreate[]
) {
  const startingPoint = startingStep ?? graph[0];
  const resultSet = new Set<TriggerStepCreate>();
  dfsSteps(startingPoint, graph, (step) => {
    resultSet.add(step);
  });
  const result = Array.from(resultSet);
  return result;
}

/**
 * Return a Map: id -> TriggerStepCreate
 *
 * @param steps
 * @returns a Map: id -> step
 */
export function getIdStepMap(steps: TriggerStepCreate[]) {
  return steps.reduce(
    (map, step) => map.set(step.id, step),
    new Map<string, TriggerStepCreate>()
  );
}

/**
 *
 * Check if the give step collides with any steps in `otherStepIds` after being moved by `delta`
 *
 * We check condition nodes too.
 *
 * @param graph
 * @param stepId
 * @param otherStepIds
 * @param delta
 * @returns
 */
export function willCollide(
  graph: TriggerStepCreate[],
  stepId: string,
  otherStepIds: Iterable<string>,
  delta = { x: 0, y: 0 }
) {
  const idStepMap = getIdStepMap(graph);
  const step = idStepMap.get(stepId);
  invariant(step, `Can't find step ${stepId}`);

  for (const otherId of otherStepIds) {
    const otherStep = idStepMap.get(otherId);
    invariant(otherStep, `Can't find step ${otherId}`);
    const nodes = step2Nodes(step);
    const nodes1 = nodes.map((node) => ({
      ...node,
      position: { x: node.position.x + delta.x, y: node.position.y + delta.y },
    }));
    const nodes2 = step2Nodes(otherStep);
    for (const n1 of nodes1) {
      for (const n2 of nodes2) {
        if (isOverlap(n1, n2)) {
          return true;
        }
      }
    }
  }
  return false;
}

function getNodeType(step: TriggerStepCreate): FlowNodeType {
  return isPlaceholderStep(step)
    ? "placeholder"
    : isIfConditionStep(step)
    ? "if-else"
    : "step";
}

function step2Nodes(s: TriggerStepCreate) {
  invariant(s.configs?.$$editorPosition);
  const nodeType = getNodeType(s);
  const result: TypedNode[] = [
    {
      id: s.id,
      type: nodeType,
      data: undefined,
      position: s.configs?.$$editorPosition,
      width: nodeSizes[nodeType].w,
      height: nodeSizes[nodeType].h,
    } as TypedNode,
  ];
  if (s.configs?.$$editorPositionWhenNode) {
    result.push({
      id: `${s.id}-when`,
      type: "condition",
      data: { stepId: s.id },
      position: s.configs.$$editorPositionWhenNode,
      width: nodeSizes["condition"].w,
      height: nodeSizes["condition"].h,
    });
  }
  return result;
}
